import { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Plus, Trash2, User, Upload, CheckCircle2, Ban, KeyRound, Copy, Target } from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const empty = { name: "", email: "", phone: "", password: "" };

// Simple CSV parser: expects header row with columns:
// name,email,password,phone (phone optional, any column order by header)
function parseCsv(text) {
  const rows = text
    .split(/\r?\n/)
    .map((r) => r.trim())
    .filter(Boolean);
  if (rows.length === 0) return { headers: [], data: [] };
  const split = (line) => line.split(",").map((c) => c.trim());
  const headers = split(rows[0]).map((h) => h.toLowerCase());
  const required = ["name", "email", "password"];
  for (const r of required) {
    if (!headers.includes(r)) {
      throw new Error(
        `CSV missing required column "${r}". Header must include: name,email,password[,phone]`,
      );
    }
  }
  const data = rows.slice(1).map((line, idx) => {
    const cells = split(line);
    const row = {};
    headers.forEach((h, i) => (row[h] = cells[i] || ""));
    return { row, lineNumber: idx + 2 };
  });
  return { headers, data };
}

export default function Salesmen() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [csvText, setCsvText] = useState(
    "name,email,password,phone\nArun Kumar,arun@distro.app,arun123,9876543210\nMeena Raj,meena@distro.app,meena123,",
  );
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [resetTarget, setResetTarget] = useState(null); // salesman row
  const [resetPwd, setResetPwd] = useState("");
  const [resetSaving, setResetSaving] = useState(false);
  const [resetDone, setResetDone] = useState(null); // {email,new_password}
  const [targetTarget, setTargetTarget] = useState(null); // salesman row
  const [targetValue, setTargetValue] = useState("");
  const [targetSaving, setTargetSaving] = useState(false);

  const openTarget = (s) => {
    setTargetTarget(s);
    setTargetValue(String(s.monthly_target || 0));
  };

  const saveTarget = async () => {
    if (!targetTarget) return;
    const v = Number(targetValue);
    if (Number.isNaN(v) || v < 0) return toast.error("Enter a valid amount");
    setTargetSaving(true);
    try {
      await api.put(`/users/salesmen/${targetTarget.id}/target`, {
        monthly_target: v,
      });
      toast.success(`Monthly target set: ${fmtINR(v)}`);
      setTargetTarget(null);
      setTargetValue("");
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setTargetSaving(false);
    }
  };

  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let s = "";
    for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return s;
  };

  const openReset = (s) => {
    setResetTarget(s);
    setResetPwd(genPassword());
    setResetDone(null);
  };

  const submitReset = async () => {
    if (!resetTarget) return;
    setResetSaving(true);
    try {
      const { data } = await api.post(
        `/users/salesmen/${resetTarget.id}/reset-password`,
        { new_password: resetPwd },
      );
      setResetDone({ email: data.email, name: data.name, new_password: data.new_password });
      toast.success(`Password reset for ${data.name || data.email}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setResetSaving(false);
    }
  };

  const closeReset = () => {
    setResetTarget(null);
    setResetPwd("");
    setResetDone(null);
  };

  const copyPwd = async () => {
    try {
      await navigator.clipboard.writeText(resetDone?.new_password || resetPwd);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const load = () => api.get("/users/salesmen").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      await api.post("/users/salesmen", form);
      toast.success("Salesman created");
      setForm(empty);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this salesman? This cannot be undone.")) return;
    await api.delete(`/users/salesmen/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggleStatus = async (s) => {
    const newActive = !(s.active !== false);
    try {
      await api.patch(`/users/salesmen/${s.id}/status`, { active: newActive });
      toast.success(newActive ? "Reactivated" : "Suspended");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
  };

  const runBulk = async () => {
    setBulkSaving(true);
    setBulkResult(null);
    try {
      const { data } = parseCsv(csvText);
      if (data.length === 0) {
        toast.error("No data rows in CSV");
        return;
      }
      const payload = {
        items: data.map(({ row }) => ({
          name: row.name,
          email: row.email,
          password: row.password,
          phone: row.phone || null,
        })),
      };
      const res = await api.post("/users/salesmen/bulk", payload);
      setBulkResult(res.data);
      toast.success(
        `Created ${res.data.created_count} · Skipped ${res.data.skipped_count}`,
      );
      load();
    } catch (e) {
      toast.error(e?.message || formatError(e));
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-salesmen-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">Team</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Salesmen
          </h1>
          <p className="text-[color:var(--muted)] mt-1">Onboard your field force.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                onClick={() => setBulkResult(null)}
                data-testid="bulk-upload-button"
              >
                <Upload className="h-4 w-4 mr-1" /> Bulk upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl" data-testid="bulk-upload-dialog">
              <DialogHeader>
                <DialogTitle>Bulk create salesmen</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-sm text-[color:var(--muted)]">
                  Upload a CSV with header row:
                  <code className="ml-1 px-1 py-0.5 bg-[color:var(--surface-2)] rounded text-xs">
                    name,email,password,phone
                  </code>
                  . Phone is optional.
                </div>
                <Input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onFile}
                  className="h-11"
                  data-testid="bulk-csv-file-input"
                />
                <Label className="text-xs text-[color:var(--muted)]">
                  Or paste CSV content below:
                </Label>
                <Textarea
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  rows={8}
                  className="font-mono text-xs"
                  data-testid="bulk-csv-textarea"
                />
                {bulkResult && (
                  <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-2)] p-3 text-sm space-y-1">
                    <div>
                      <b className="text-[color:var(--moss)]">
                        ✓ {bulkResult.created_count} created
                      </b>
                      {bulkResult.skipped_count > 0 && (
                        <span className="ml-3 text-[color:var(--primary)]">
                          ✗ {bulkResult.skipped_count} skipped
                        </span>
                      )}
                    </div>
                    {bulkResult.skipped?.length > 0 && (
                      <ul className="text-xs text-[color:var(--muted)] list-disc pl-5">
                        {bulkResult.skipped.map((s, i) => (
                          <li key={i}>
                            {s.email} — {s.reason}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  onClick={runBulk}
                  disabled={bulkSaving}
                  className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                  data-testid="bulk-upload-submit"
                >
                  {bulkSaving ? "Uploading…" : "Upload"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => setForm(empty)}
                className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                data-testid="add-salesman-button"
              >
                <Plus className="h-4 w-4 mr-1" /> Add salesman
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="salesman-dialog">
              <DialogHeader>
                <DialogTitle>New salesman</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="salesman-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="salesman-email-input"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Temporary password</Label>
                  <Input
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="salesman-password-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={save}
                  className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                  data-testid="save-salesman-button"
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => {
          const active = s.active !== false;
          return (
            <div
              key={s.id}
              className={`rounded-xl border bg-white p-5 ink-shadow ${
                active ? "border-[color:var(--border)]" : "border-[color:var(--primary)]/30 bg-[color:var(--primary)]/[0.03]"
              }`}
              data-testid={`salesman-card-${s.id}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 ${
                    active
                      ? "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
                      : "bg-[color:var(--muted)]/10 text-[color:var(--muted)]"
                  }`}
                >
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-semibold truncate">{s.name}</div>
                    {!active && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--primary)]/10 text-[color:var(--primary)] uppercase tracking-wide">
                        Suspended
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--muted)] truncate">{s.email}</div>
                  {s.phone && <div className="text-xs text-[color:var(--muted)]">{s.phone}</div>}
                </div>
                <button
                  onClick={() => remove(s.id)}
                  className="p-1.5 text-[color:var(--muted)] hover:text-[color:var(--primary)] shrink-0"
                  data-testid={`delete-salesman-${s.id}`}
                  aria-label="Delete salesman"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-[color:var(--border)] pt-3">
                <div className="flex items-center gap-2">
                  {active ? (
                    <CheckCircle2 className="h-4 w-4 text-[color:var(--moss)]" />
                  ) : (
                    <Ban className="h-4 w-4 text-[color:var(--muted)]" />
                  )}
                  <span className="text-sm">{active ? "Active" : "Suspended"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={active}
                    onCheckedChange={() => toggleStatus(s)}
                    data-testid={`toggle-status-${s.id}`}
                  />
                  <button
                    onClick={() => toggleStatus(s)}
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      active
                        ? "text-[color:var(--primary)] hover:text-[color:var(--primary-hover)]"
                        : "text-[color:var(--moss)] hover:opacity-80"
                    }`}
                    data-testid={`toggle-status-btn-${s.id}`}
                  >
                    {active ? "Suspend" : "Reactivate"}
                  </button>
                </div>
              </div>

              <button
                onClick={() => openReset(s)}
                className="mt-3 w-full flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wide rounded-lg border border-[color:var(--border)] py-2 text-[color:var(--ink)] hover:bg-[color:var(--surface-2)]"
                data-testid={`reset-password-btn-${s.id}`}
              >
                <KeyRound className="h-4 w-4" /> Reset password
              </button>

              <button
                onClick={() => openTarget(s)}
                className="mt-2 w-full flex items-center justify-between gap-2 text-xs font-semibold rounded-lg border border-[color:var(--border)] py-2 px-3 text-[color:var(--ink)] hover:bg-[color:var(--surface-2)]"
                data-testid={`set-target-btn-${s.id}`}
              >
                <span className="flex items-center gap-2 uppercase tracking-wide">
                  <Target className="h-4 w-4" /> Monthly target
                </span>
                <span className="font-display text-sm normal-case tracking-normal">
                  {s.monthly_target ? fmtINR(s.monthly_target) : "Not set"}
                </span>
              </button>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-full text-center py-12 text-[color:var(--muted)] rounded-xl border border-dashed border-[color:var(--border)]">
            No salesmen yet. Add your first.
          </div>
        )}
      </div>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && closeReset()}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>
              Reset password{resetTarget ? ` · ${resetTarget.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          {!resetDone ? (
            <div className="space-y-3">
              <div className="text-sm text-[color:var(--muted)]">
                Set a new password for{" "}
                <b className="text-[color:var(--ink)]">{resetTarget?.email}</b>. Share it
                with the salesman directly — they can change it later from their profile.
              </div>
              <div>
                <Label>New password</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={resetPwd}
                    onChange={(e) => setResetPwd(e.target.value)}
                    className="font-mono"
                    data-testid="reset-password-input"
                    autoFocus
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setResetPwd(genPassword())}
                    data-testid="reset-password-generate"
                  >
                    Generate
                  </Button>
                </div>
                <div className="text-xs text-[color:var(--muted)] mt-1">
                  Min 4 characters.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeReset}>
                  Cancel
                </Button>
                <Button
                  onClick={submitReset}
                  disabled={resetSaving || resetPwd.length < 4}
                  className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                  data-testid="reset-password-submit"
                >
                  {resetSaving ? "Resetting…" : "Reset password"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid="reset-password-success">
              <div className="rounded-lg bg-[color:var(--moss)]/10 border border-[color:var(--moss)]/30 p-3 text-sm">
                Password updated for{" "}
                <b>{resetDone.name || resetDone.email}</b>. Share these credentials with
                the salesman:
              </div>
              <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-2)] p-3 font-mono text-sm space-y-1">
                <div>
                  <span className="text-[color:var(--muted)]">Email:</span>{" "}
                  {resetDone.email}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[color:var(--muted)]">Password:</span>
                  <span className="font-bold">{resetDone.new_password}</span>
                  <button
                    onClick={copyPwd}
                    className="ml-auto inline-flex items-center gap-1 text-xs text-[color:var(--ink)] hover:text-[color:var(--primary)]"
                    data-testid="reset-password-copy"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </button>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={closeReset}
                  className="bg-[color:var(--ink)] hover:bg-black text-white"
                  data-testid="reset-password-done"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!targetTarget} onOpenChange={(o) => !o && setTargetTarget(null)}>
        <DialogContent data-testid="target-dialog">
          <DialogHeader>
            <DialogTitle>
              Monthly target{targetTarget ? ` · ${targetTarget.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[color:var(--muted)]">
              Set this salesman's revenue goal for the current calendar month. Enter 0
              to disable target tracking.
            </div>
            <div>
              <Label>Target amount (INR)</Label>
              <Input
                type="number"
                min="0"
                step="500"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                className="font-mono h-12"
                data-testid="target-value-input"
                autoFocus
              />
              {Number(targetValue) > 0 && (
                <div className="text-xs text-[color:var(--muted)] mt-1">
                  ≈ {fmtINR(Number(targetValue))} this month
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTargetTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={saveTarget}
                disabled={targetSaving}
                className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                data-testid="target-save-btn"
              >
                {targetSaving ? "Saving…" : "Save target"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
